import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  getMessaging,
  isSupported,
  onMessage,
  onRegistered,
  onUnregistered,
  register,
  unregister
} from 'firebase/messaging';

import firebaseApp, { vapidKey } from './firebase';

const FirebaseMessagingContext = createContext(null);

export function FirebaseMessagingProvider({
  children,
  onInstallationRegistered,
  onInstallationUnregistered,
  onNotification
}) {
  const [isSupportedBrowser, setIsSupportedBrowser] = useState(false);

  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  const [fid, setFid] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState(null);
  const [lastNotification, setLastNotification] = useState(null);

  const messagingRef = useRef(null);
  const permissionRequestedRef = useRef(false);
  const registeringRef = useRef(false);
  const callbacksRef = useRef({});

  // Always use the latest callbacks without re-registering listeners.
  callbacksRef.current = {
    onInstallationRegistered,
    onInstallationUnregistered,
    onNotification
  };

  const registerDevice = useCallback(async () => {
    if (registeringRef.current) return false;

    registeringRef.current = true;
    setIsRegistering(true);
    setError(null);

    try {
      const supported = await isSupported();

      if (!supported) {
        throw new Error('Firebase Messaging is not supported');
      }

      if (Notification.permission !== 'granted') {
        return false;
      }

      if (!vapidKey) {
        throw new Error('Firebase VAPID key is missing');
      }

      const messaging = messagingRef.current || getMessaging(firebaseApp);

      messagingRef.current = messaging;

      const swRegistration = await navigator.serviceWorker.getRegistration('/');

      if (!swRegistration) {
        throw new Error('Moneyx Service Worker is not registered');
      }

      await register(messaging, {
        vapidKey,
        serviceWorkerRegistration: swRegistration
      });

      return true;
    } catch (err) {
      console.error('[FCM] Registration failed:', err);
      setError(err.message);
      return false;
    } finally {
      registeringRef.current = false;
      setIsRegistering(false);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    try {
      if (!(await isSupported())) {
        setError('Push notifications are not supported');
        return false;
      }

      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        return false;
      }

      return await registerDevice();
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [registerDevice]);

  const disableNotifications = useCallback(async () => {
    try {
      if (messagingRef.current) {
        await unregister(messagingRef.current);
      }

      setFid(null);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, []);

  useEffect(() => {
    let active = true;
    const unsubscribers = [];

    async function initialize() {
      try {
        const supported = await isSupported();

        if (!active) return;

        setIsSupportedBrowser(supported);

        if (!supported) return;

        const messaging = getMessaging(firebaseApp);
        messagingRef.current = messaging;

        const unsubRegistered = onRegistered(messaging, (installationId) => {
          if (!active) return;

          setFid(installationId);

          Promise.resolve(
            callbacksRef.current.onInstallationRegistered?.(installationId)
          ).catch(console.error);
        });

        unsubscribers.push(unsubRegistered);

        const unsubUnregistered = onUnregistered(
          messaging,
          (installationId) => {
            if (!active) return;

            setFid(null);

            Promise.resolve(
              callbacksRef.current.onInstallationUnregistered?.(installationId)
            ).catch(console.error);
          }
        );

        unsubscribers.push(unsubUnregistered);

        const unsubMessage = onMessage(messaging, (payload) => {
          if (!active) return;

          setLastNotification(payload);

          callbacksRef.current.onNotification?.(payload);
        });

        unsubscribers.push(unsubMessage);
        const currentPermission = Notification.permission;

        setPermission(currentPermission);

        if (currentPermission === 'granted') {
          await registerDevice();
        } else if (
          currentPermission === 'default' &&
          !permissionRequestedRef.current
        ) {
          permissionRequestedRef.current = true;

          try {
            const result = await Notification.requestPermission();

            if (!active) return;

            setPermission(result);

            if (result === 'granted') {
              await registerDevice();
            }
          } catch (err) {
            console.error('[FCM] Permission request failed:', err);

            if (active) {
              setError(err.message);
            }
          }
        }
      } catch (err) {
        if (active) setError(err.message);
      }
    }

    initialize();

    return () => {
      active = false;

      unsubscribers.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [registerDevice]);

  const contextValue = useMemo(
    () => ({
      isSupported: isSupportedBrowser,
      permission,
      fid,
      isRegistering,
      error,
      lastNotification,
      requestPermission,
      registerDevice,
      disableNotifications
    }),
    [
      isSupportedBrowser,
      permission,
      fid,
      isRegistering,
      error,
      lastNotification,
      requestPermission,
      registerDevice,
      disableNotifications
    ]
  );

  return (
    <FirebaseMessagingContext.Provider value={contextValue}>
      {children}
    </FirebaseMessagingContext.Provider>
  );
}

export function useFirebaseMessaging() {
  const context = useContext(FirebaseMessagingContext);

  if (!context) {
    throw new Error(
      'useFirebaseMessaging must be used inside FirebaseMessagingProvider'
    );
  }

  return context;
}
